import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { map, isEmpty, concat, remove } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';

import { SIMMONS_DATA } from '../../constants';
import { PARENT_CHILDREN_LABEL_SEPARATOR } from './RldConstant';
import RldToolDictionaryTree from './RldToolDictionaryTree/RldToolDictionaryTree';
import GenericButton from '../common/GenericButton';
import {
  getDictionaryTreeLoad,
  setDictionaryTargetTree,
} from '../../actions/rldToolActions';
import {
  findTreeNode,
  flattenTreeNodeLablesWithParent,
  getDeepTreeCopy,
  selectTreeNodesBetweenNodes,
  ignoreDuplicatesBetweenTreeNodes,
  removeTreeNode,
} from '../common/DynamicTree/DynamicTreeUtils';

const useStyles = makeStyles(theme => {
  return {
    root: {
      display: 'flex',
      padding: theme.spacing(3),
    },
  };
});

const RldToolTransferrableList = () => {
  const classes = useStyles();
  const [itemsLeft, setItemsLeft] = useState([]);
  const [selectedItemsLeft, setSelectedItemsLeft] = useState([]);
  const [itemsRight, setItemsRight] = useState([]);
  const [selectedItemsRight, setSelectedItemsRight] = useState([]);
  const [flatTreeNodeLabels, setFlatTreeNodeLabels] = useState({});

  const { selectedAccount, dictionaryTree, selectedStudy, uid } = useSelector(
    state => ({
      selectedAccount: state.rldTool.account.selectedAccount,
      dictionaryTree: state.rldTool.dictionary.dictionaryTree,
      selectedStudy: state.rldTool.study.selectedStudy.pn,
      uid: state.login.loginResponse.data.uid,
    })
  );

  const nodeID = 0;
  const filter = SIMMONS_DATA;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEmpty(selectedAccount) && selectedStudy) {
      // To remove all selected items when there is change in selectedAccount and/or selectedStudy
      setItemsRight([]);
      setItemsLeft([]);
      setSelectedItemsRight([]);
      setSelectedItemsLeft([]);

      dispatch(
        getDictionaryTreeLoad(
          uid,
          selectedAccount.accountId,
          selectedStudy,
          nodeID,
          filter
        )
      );
    }
  }, [selectedAccount, selectedStudy]);

  useEffect(() => {
    const deepTreeCopyDictionaryTree = getDeepTreeCopy(dictionaryTree);
    setItemsLeft(deepTreeCopyDictionaryTree);
    let newFlatTreeNodeLabels;
    if (deepTreeCopyDictionaryTree) {
      newFlatTreeNodeLabels = flattenTreeNodeLablesWithParent(
        deepTreeCopyDictionaryTree,
        {},
        PARENT_CHILDREN_LABEL_SEPARATOR
      );
    } else {
      newFlatTreeNodeLabels = {};
    }

    setFlatTreeNodeLabels(newFlatTreeNodeLabels);
  }, [dictionaryTree]);

  useEffect(() => {
    const deepCopyTreeData = getDeepTreeCopy(itemsRight);
    dispatch(setDictionaryTargetTree(deepCopyTreeData));
  }, [itemsRight]);

  const changeItemsRightLabel = (node, flatTreeNodeLabels) => {
    if (node.shortText && node.textPath) {
      node.shortText = flatTreeNodeLabels[node.textPath];
    }

    const nodeArray = node.children ? node.children : node;
    if (nodeArray) {
      for (let i = 0; i < nodeArray.length; i += 1) {
        nodeArray[i] = changeItemsRightLabel(nodeArray[i], flatTreeNodeLabels);
      }
    }

    return node;
  };

  const addSelected = () => {
    let prevItemsRight = itemsRight;

    // Remove duplicate sub-categories if already been added
    // duplicates to be removed from itemsRight, NOT on the selectedItemsLeft
    // selectedItemsLeft can be list,
    // so, Need to loop through all selectedItemsLeft's sub-category
    for (let i = 0; i < selectedItemsLeft.length; i += 1) {
      // findTreeNode is used to get current complete node (selectedItemsLeft[i].node)
      // to determine whether current node has the children
      const foundItem = findTreeNode(dictionaryTree, selectedItemsLeft[i].node);

      // if there are children (Sub-category) for the foundItem
      // Remove them from itemsRight
      if (foundItem && foundItem.children) {
        removeTreeNode(foundItem.children, itemsRight);
        prevItemsRight = itemsRight;
      }
    }

    const newItemsRight = concat(prevItemsRight, selectedItemsLeft);
    const deepCopyItemsRight = getDeepTreeCopy(newItemsRight);
    const labelChangedItemsRight = changeItemsRightLabel(
      deepCopyItemsRight,
      flatTreeNodeLabels
    );

    // setItemsLeft(itemsRemaining);
    setItemsRight(labelChangedItemsRight);
    setSelectedItemsRight([]);
    setSelectedItemsLeft([]);
  };

  const removeSelected = () => {
    const itemsRemaining = [];
    // const newItemsLeft = [];

    // need to make change to loop through all selected Itmes
    for (let i = 0; i < itemsRight.length; i += 1) {
      const foundItem = findTreeNode(selectedItemsRight, itemsRight[i].node);

      // To keep only the items required
      if (foundItem) {
        // newItemsLeft.push(itemsRight[i]);
      } else {
        itemsRemaining.push(itemsRight[i]);
      }
    }

    // setItemsLeft(newItemsLeft);
    setItemsRight(itemsRemaining);
    setSelectedItemsRight([]);
    setSelectedItemsLeft([]);
  };

  const removeAll = () => {
    setItemsRight([]);
    setSelectedItemsRight([]);
    setSelectedItemsLeft([]);
  };

  const handleRightPanelClick = (node, clearExisting, shiftKey) => {
    let selectedItems = [];
    const newNode = findTreeNode(itemsRight, node);

    if (!shiftKey) {
      if (!clearExisting) {
        selectedItems = selectedItemsRight;
      }

      selectedItems.push(newNode);

      // To ignore children node
      const newSelectedItems = selectedItems.map(
        ({ children, ...item }) => item
      );
      setSelectedItemsRight(newSelectedItems);
    } else {
      selectedItems = selectedItemsRight;
      const lastItem = selectedItemsRight.pop();

      selectTreeNodesBetweenNodes(lastItem, newNode, itemsRight, selectedItems);
      // To ignore children node
      const newSelectedItems = selectedItems.map(
        ({ children, ...item }) => item
      );
      setSelectedItemsRight(newSelectedItems);
    }
  };

  const handleLeftPanelClick = (node, clearExisting, shiftKey) => {
    let selectedItems = [];
    const newNode = findTreeNode(itemsLeft, node);

    if (!shiftKey) {
      if (!clearExisting) {
        selectedItems = selectedItemsLeft;
      }

      selectedItems.push(newNode);
      // To ignore children node
      const newSelectedItems = selectedItems.map(
        ({ children, ...item }) => item
      );

      // If only selected node does not present in itemsRight proceed to add
      // otherwise skip the process
      const ignoredList = [];
      ignoreDuplicatesBetweenTreeNodes(
        newSelectedItems,
        itemsRight,
        ignoredList
      );
      setSelectedItemsLeft([...ignoredList]);
    } else {
      selectedItems = selectedItemsLeft;
      const lastItem = selectedItemsLeft.pop();

      selectTreeNodesBetweenNodes(lastItem, newNode, itemsLeft, selectedItems);

      // To ignore children node
      const newSelectedItems = selectedItems.map(
        ({ children, ...item }) => item
      );

      // If only selected node does not present in itemsRight proceed to add
      // otherwise skip the process
      const ignoredList = [];
      ignoreDuplicatesBetweenTreeNodes(
        newSelectedItems,
        itemsRight,
        ignoredList
      );
      setSelectedItemsLeft([...ignoredList]);
    }
  };

  const handleMoveAllRight = () => {
    const allItemsToRight = itemsLeft;
    setItemsRight(allItemsToRight);
    setSelectedItemsLeft([]);
    setSelectedItemsRight([]);
  };

  const handleMoveAllLeft = () => {
    setItemsRight([]);
    setSelectedItemsRight([]);
    setSelectedItemsLeft([]);
  };

  const handleSelectedMoveUp = () => {
    const deepCopyItemsRight = getDeepTreeCopy(itemsRight);

    // need to make change to loop through all selected Itmes
    const index = deepCopyItemsRight.findIndex(
      currentItem => currentItem.node === selectedItemsRight[0].node
    );
    if (index > 0) {
      const currentItem = deepCopyItemsRight[index];
      deepCopyItemsRight[index] = deepCopyItemsRight[index - 1];
      deepCopyItemsRight[index - 1] = currentItem;
    }

    setItemsRight(deepCopyItemsRight);
  };

  const handleSelectedMoveDown = () => {
    const deepCopyItemsRight = getDeepTreeCopy(itemsRight);

    // need to make change to loop through all selected Itmes
    const index = deepCopyItemsRight.findIndex(
      currentItem => currentItem.node === selectedItemsRight[0].node
    );

    if (index !== -1 && index < deepCopyItemsRight.length - 1) {
      const currentItem = deepCopyItemsRight[index];
      deepCopyItemsRight[index] = deepCopyItemsRight[index + 1];
      deepCopyItemsRight[index + 1] = currentItem;
    }

    setItemsRight(deepCopyItemsRight);
  };

  return (
    <Grid container className={classes.root} pt={3} pl={1}>
      <Grid item md={5}>
        <RldToolDictionaryTree
          uid={uid}
          items={itemsLeft}
          selectedItems={selectedItemsLeft}
          itemsOnOtherSide={itemsRight}
          handleSelection={handleLeftPanelClick}
          selectedAccount={selectedAccount}
          selectedStudy={selectedStudy}
          isExpandable={true}
        />
      </Grid>
      <Grid
        item
        md={1}
        className="stacked button-group multi-select-button-group"
      >
        <GenericButton
          name=" "
          btnStyle="button btn-add btn-priaryDisable"
          onBtnClick={addSelected}
          {...(selectedItemsLeft && selectedItemsLeft.length < 1
            ? { btnDisabled: true }
            : {})}
        />
        <GenericButton
          name=" "
          btnStyle="button btn-add-all btn-priaryDisable"
          onBtnClick={handleMoveAllRight}
          {...(itemsLeft && itemsLeft.length < 1 ? { btnDisabled: true } : {})}
        />
        <GenericButton
          name=" "
          btnStyle="button btn-remove btn-cncl"
          onBtnClick={removeSelected}
          {...(selectedItemsRight && selectedItemsRight.length < 1
            ? { btnDisabled: true }
            : {})}
        />
        <GenericButton
          name=" "
          btnStyle="button btn-remove-all btn-priaryDisable"
          onBtnClick={handleMoveAllLeft}
          {...(itemsRight && itemsRight.length < 1
            ? { btnDisabled: true }
            : {})}
        />
        <GenericButton
          name=" "
          btnStyle="button btn-up btn-priaryDisable"
          onBtnClick={handleSelectedMoveUp}
          {...(selectedItemsRight && selectedItemsRight.length !== 1
            ? { btnDisabled: true }
            : {})}
        />
        <GenericButton
          name=" "
          btnStyle="button btn-down btn-priaryDisable"
          onBtnClick={handleSelectedMoveDown}
          {...(selectedItemsRight && selectedItemsRight.length !== 1
            ? { btnDisabled: true }
            : {})}
        />
      </Grid>
      <Grid item md={6}>
        <RldToolDictionaryTree
          uid={uid}
          items={itemsRight}
          selectedItems={selectedItemsRight}
          itemsOnOtherSide={itemsLeft}
          handleSelection={handleRightPanelClick}
          selectedAccount={selectedAccount}
          selectedStudy={selectedStudy}
        />
      </Grid>
    </Grid>
  );
};

export default RldToolTransferrableList;
